import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getChartOfAccounts, deleteChartOfAccount } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SearchInput from "../components/UI/SearchInput";
import ConfirmModal from "../components/UI/ConfirmModal";
import { notify } from "../utils/notify";
import DataTable from "../components/UI/DataTable";
import PageHeader from "../components/UI/PageHeader";
import { I18nContext, useTranslation } from "../context/I18nContext";

const TYPE_BADGE = {
  asset: "bg-blue-50 text-blue-600",
  liability: "bg-amber-50 text-amber-600",
  equity: "bg-purple-50 text-purple-600",
  revenue: "bg-green-50 text-green-600",
  expense: "bg-red-50 text-red-500",
};

export default function ChartOfAccounts() {
  // Account names can come back translated per the signed-in user's own
  // language (see ResolveEntityNames in entity_translation_handler.go) —
  // that resolution happens server-side at fetch time, so this list needs
  // to re-fetch whenever languageCode changes, not just on mount — same
  // reasoning as Categories.js.
  const { languageCode } = useContext(I18nContext);
  const { t } = useTranslation("accounting");
  const { t: tCommon } = useTranslation("common");
  const [accounts, setAccounts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "code", direction: "asc" });
  const [modalConfig, setModalConfig] = useState({ isOpen: false, id: null });
  const [status, setStatus] = useState("idle");

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const fetchAccounts = async () => {
    try {
      const data = await getChartOfAccounts(token);
      setAccounts(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchAccounts(); }, [languageCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedAndFiltered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    let result = [...accounts].filter(
      (a) => a.code.toLowerCase().includes(term) || a.name.toLowerCase().includes(term)
    );
    result.sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return result;
  }, [accounts, searchTerm, sortConfig]);

  const currentItems = sortedAndFiltered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const totalPages = Math.ceil(sortedAndFiltered.length / perPage) || 1;

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  const handleDeleteClick = (id) => setModalConfig({ isOpen: true, id });

  const confirmDelete = async () => {
    try {
      setStatus("loading");
      await deleteChartOfAccount(token, modalConfig.id);
      setStatus("success");
      setTimeout(() => {
        setModalConfig({ isOpen: false, id: null });
        setStatus("idle");
      }, 1200);
      notify.success(t("notify_deleted", "Account deleted successfully"));
      fetchAccounts();
    } catch (err) {
      setStatus("error");
      notify.error(err.data?.error || err.message || t("notify_delete_failed", "Failed to delete account"));
    }
  };

  const columns = [
    { key: "code", label: t("col_code", "Code"), className: "text-gray-400 font-mono" },
    {
      key: "name",
      label: t("col_name", "Account"),
      render: (row) => (
        <div className="flex items-center gap-2 font-bold">
          {row.name}
          {row.system_key && (
            <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase bg-gray-100 text-gray-500" title={t("system_account_hint", "Used automatically by the accounting engine")}>
              {t("badge_system", "System")}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "type",
      label: t("col_type", "Type"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${TYPE_BADGE[row.type] || "bg-gray-50 text-gray-500"}`}>
          {t(`account_type_${row.type}`, row.type)}
        </span>
      ),
    },
    {
      key: "parent",
      label: t("col_parent", "Parent"),
      className: "text-gray-600",
      render: (row) => row.parent?.name || row.parent?.code || t("parent_top_level", "— (top level)"),
    },
    {
      key: "is_active",
      label: tCommon("table_col_status", "Status"),
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${row.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}>
          {row.is_active ? tCommon("status_active", "Active") : tCommon("status_inactive", "Inactive")}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title={t("delete_confirm_title", "Delete Account")}
        message={t("delete_confirm_message", "Are you sure? This will fail if the account is a system account, has sub-accounts, or already has journal entries posted to it.")}
        onConfirm={confirmDelete}
        onCancel={() => setModalConfig({ isOpen: false, id: null })}
        isLoading={status === "loading"}
        isSuccess={status === "success"}
        isError={status === "error"}
      />

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.ChartOfAccounts size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Chart of Accounts")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Every account the accounting system can post to")}</p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
          <SearchInput
            value={searchTerm}
            onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
            placeholder={t("search_placeholder", "Search by code or name...")}
          />
          <Button variant="primary" icon={ICONS.Plus} onClick={() => navigate("/add-account")} className="w-full sm:w-auto shrink-0">
            {t("btn_add_account", "Add Account")}
          </Button>
        </div>
      </PageHeader>

      <DataTable
        columns={columns}
        data={currentItems}
        sortConfig={sortConfig}
        onSort={requestSort}
        renderActions={(row, isMobile) => (
          <>
            <Button variant="outline" size="sm" icon={ICONS.EditUser} onClick={() => navigate(`/edit-account/${row.id}`)}>{isMobile && ` ${tCommon("btn_edit", "Edit")}`}</Button>
            <Button variant="danger" size="sm" icon={ICONS.DeleteUser} onClick={() => handleDeleteClick(row.id)}>{isMobile && ` ${tCommon("btn_delete", "Delete")}`}</Button>
          </>
        )}
        mobileRender={(row) => (
          <div className="flex justify-between items-center">
            <span className="text-[10px] text-gray-400 font-mono">{row.code}</span>
            <h3 className="font-bold text-gray-800">{row.name}</h3>
          </div>
        )}
        paginationConfig={{
          currentPage,
          totalPages,
          onPageChange: (p) => setCurrentPage(p),
          totalItems: sortedAndFiltered.length,
        }}
      />
    </div>
  );
}
